import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Seat management for a company administrator. Rendered inside the dashboard,
// so the customer never sees Django admin: IDTA sells the seats, the customer
// decides who fills them.
function SeatManager() {
  const [licence, setLicence] = useState(null);
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAdmin, setNotAdmin] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/seats');
      setLicence(data.licence);
      setSeats(data.seats || []);
      setNotAdmin(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setNotAdmin(true);
      } else {
        setError(err?.response?.data?.message || 'Could not load your licence.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addPerson = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post('/api/seats', {
        email: email.trim(),
        full_name: fullName.trim(),
      });
      setSeats((prev) => [...prev, data.seat]);
      setLicence(data.licence);
      setEmail('');
      setFullName('');
    } catch (err) {
      // The service returns a sentence the admin can act on ("All 5 seats are
      // in use...") so it is shown rather than replaced.
      setError(err?.response?.data?.message || 'Could not add that person.');
    } finally {
      setBusy(false);
    }
  };

  const removePerson = async (seat) => {
    if (!window.confirm(`Remove ${seat.email}? They will lose access immediately.`)) return;
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.delete(`/api/seats/${seat.id}`);
      setSeats((prev) => prev.filter((s) => s.id !== seat.id));
      setLicence(data.licence);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not remove that person.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="text-[#64748b]">Loading your licence…</p>;
  }

  // Members see nothing to manage; only the administrator gets this panel.
  if (notAdmin) {
    return (
      <div className="rounded-xl border border-[#e2e8f0] bg-[#f8f9fc] p-6">
        <h3 className="font-semibold mb-2">Team access</h3>
        <p className="text-[#64748b] text-sm">
          Your company administrator manages who has access to ADX Hub.
        </p>
      </div>
    );
  }

  const full = licence && licence.seats_available === 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold mb-1">Team access</h3>
        <p className="text-[#64748b]">
          {licence.company_name} · <strong>{licence.seats_used} of {licence.seat_limit}</strong> seats in use
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 text-sm">{error}</div>
      )}

      <ul className="divide-y divide-[#e2e8f0] rounded-xl border border-[#e2e8f0]">
        {seats.map((seat) => (
          <li key={seat.id} className="flex items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <p className="font-medium truncate">{seat.full_name || seat.email}</p>
              <p className="text-sm text-[#64748b] truncate">
                {seat.full_name ? `${seat.email} · ` : ''}
                {seat.role === 'admin' ? 'Administrator' : 'Member'}
                {seat.has_signed_in ? '' : ' · not signed in yet'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => removePerson(seat)}
              disabled={busy}
              className="shrink-0 text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-50"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={addPerson} className="space-y-4">
        <h4 className="font-semibold">Add someone</h4>
        {full && (
          <p className="text-sm text-[#64748b]">
            All seats are in use. Remove someone first, or contact IDTA to add seats.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            disabled={full || busy}
            className="w-full px-4 py-3 bg-[#f8f9fc] border border-[#e2e8f0] rounded-xl focus:outline-none focus:border-[#0028CD] disabled:opacity-50"
          />
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name (optional)"
            disabled={full || busy}
            className="w-full px-4 py-3 bg-[#f8f9fc] border border-[#e2e8f0] rounded-xl focus:outline-none focus:border-[#0028CD] disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          disabled={full || busy}
          className="px-6 py-3 bg-[#0028CD] text-white rounded-xl font-semibold hover:bg-[#001F9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Saving…' : 'Add to team'}
        </button>
        <p className="text-sm text-[#64748b]">
          They sign in with their company Microsoft account. Anyone not listed
          here cannot access ADX Hub.
        </p>
      </form>
    </div>
  );
}

export default SeatManager;
